/* eslint-disable no-restricted-imports -- rexports banned imports */
import { createDraftSafeSelectorCreator } from "@reduxjs/toolkit";
import deepEqual from "fast-deep-equal";
import { lruMemoize, weakMapMemoize } from "reselect";

import { shallowEqual } from "../../util/index.js";

// we rexport this to allow easy global configuration in the future
export { createDraftSafeSelectorCreator as createSelectorCreator } from "@reduxjs/toolkit";

const sharedSelectorOptions = {
  devModeChecks: {
    // Our usage of `stableEmptyArray` and `stableEmptyObject` causes a fair amount
    // of spurious warnings with this check
    identityFunctionCheck: "never",
  },
} as const;

const selectorOptions = {
  ...sharedSelectorOptions,
  memoize: lruMemoize,
  argsMemoize: lruMemoize,
} as const;

const selectorV2Options = {
  ...sharedSelectorOptions,
  memoize: weakMapMemoize,
  argsMemoize: weakMapMemoize,
} as const;

/**
 * Thin wrapper around {@link https://reselect.js.org/api/createselector/ | `createSelector`}
 * that adds hex specific configuration and  {@link https://redux-toolkit.js.org/api/createSelector#createdraftsafeselector | draft safety}
 *
 * @deprecated uses legacy lru memoization, prefer {@link createSelectorV2 | `createSelectorV2`}
 */
export const createSelector = createDraftSafeSelectorCreator({
  ...selectorOptions,
});

/**
 * Thin wrapper around {@link https://reselect.js.org/api/createselector/ | `createSelector`}
 * that adds hex specific configuration and  {@link https://redux-toolkit.js.org/api/createSelector#createdraftsafeselector | draft safety}
 *
 * Uses the newer weak map memoization
 */
export const createSelectorV2 = createDraftSafeSelectorCreator({
  ...selectorV2Options,
});

/**
 * Creates a selector that compares the result of its output selector with
 * `shallowEqual`. Same as passing `memoizeOptions.resultEqualityCheck: shallowEqual`
 * to {@link createSelector | `createSelector`}
 *
 * This will have the same effect as calling the selector with
 * `useSelector(selector, shallowEqual)`, but bakes in the shallowness of the
 * comparison to the selector itself (instead of relying on the consumer). This
 * can also be used when chaining re-select selectors to make an input
 * selector shallow compared.
 *
 * @deprecated uses legacy lru memoization, prefer {@link createShallowResultSelectorV2 | `createShallowResultSelectorV2`}
 */
export const createShallowResultSelector = createDraftSafeSelectorCreator({
  ...selectorOptions,
  memoizeOptions: {
    resultEqualityCheck: shallowEqual,
  },
});

/**
 * Creates a selector that compares the result of its output selector with
 * `shallowEqual`. Same as passing `memoizeOptions.resultEqualityCheck: shallowEqual`
 * to {@link createSelectorV2 | `createSelectorV2`}
 *
 * This will have the same effect as calling the selector with
 * `useSelector(selector, shallowEqual)`, but bakes in the shallowness of the
 * comparison to the selector itself (instead of relying on the consumer). This
 * can also be used when chaining re-select selectors to make an input
 * selector shallow compared.
 *
 * Uses the newer weak map memoization
 */
export const createShallowResultSelectorV2 = createDraftSafeSelectorCreator({
  ...selectorV2Options,
  memoizeOptions: {
    resultEqualityCheck: shallowEqual,
  },
});

/**
 * Creates a selector that compares the result of its input selectors using
 * `shallowEqual`. Same as passing `argsMemoizeOptions.resultEqualityCheck: shallowEqual`
 * to {@link createSelector | `createSelector`}
 *
 * This will have the same effect as using {@link createShallowResultSelector | `createShallowResultSelector`}
 * to define all of the input selectors. You should generally prefer just using `createShallowResultSelector`
 * on all input selectors
 *
 * @deprecated uses legacy lru memoization, prefer {@link createShallowInputsSelectorV2 | `createShallowInputsSelectorV2`}
 */
export const createShallowInputsSelector = createDraftSafeSelectorCreator({
  ...selectorOptions,
  argsMemoizeOptions: {
    resultEqualityCheck: shallowEqual,
  },
});

/**
 * Creates a selector that compares the result of its input selectors using
 * `shallowEqual`. Same as passing `argsMemoizeOptions.resultEqualityCheck: shallowEqual`
 * to {@link createSelectorV2 | `createSelectorV2`}
 *
 * This will have the same effect as using {@link createShallowResultSelectorV2 | `createShallowResultSelectorV2`}
 * to define all of the input selectors. You should generally prefer just using `createShallowResultSelectorV2`
 * on all input selectors
 *
 * Uses the newer weak map memoization
 */
export const createShallowInputsSelectorV2 = createDraftSafeSelectorCreator({
  ...selectorV2Options,
  argsMemoizeOptions: {
    resultEqualityCheck: shallowEqual,
  },
});

/**
 * Creates a selector that compares the result of both input and output
 * selectors with `shallowEqual`. Same as passing `argsMemoizeOptions.resultEqualityCheck: shallowEqual`
 * and `argsMemoizeOptions.resultEqualityCheck: shallowEqual`
 * to {@link createSelector | `createSelector`}
 *
 * This is the combination of both {@link createShallowInputsSelector | `createShallowInputsSelector`} and
 * {@link createShallowResultSelector | `createShallowResultSelector`}.
 *
 * Generally should prefer having the entire selector chain use {@link createShallowResultSelector | `createShallowResultSelector`} instead
 *
 * @deprecated uses legacy lru memoization, prefer {@link createShallowInputsAndResultSelectorV2 | `createShallowInputsAndResultSelectorV2`}
 */
export const createShallowInputsAndResultSelector =
  createDraftSafeSelectorCreator({
    ...selectorOptions,
    memoizeOptions: {
      resultEqualityCheck: shallowEqual,
    },
    argsMemoizeOptions: {
      resultEqualityCheck: shallowEqual,
    },
  });

/**
 * Creates a selector that compares the result of both input and output
 * selectors with `shallowEqual`. Same as passing `argsMemoizeOptions.resultEqualityCheck: shallowEqual`
 * and `argsMemoizeOptions.resultEqualityCheck: shallowEqual`
 * to {@link createSelectorV2 | `createSelectorV2`}
 *
 * This is the combination of both {@link createShallowInputsSelectorV2 | `createShallowInputsSelectorV2`} and
 * {@link createShallowResultSelectorV2 | `createShallowResultSelectorV2`}.
 *
 * Generally should prefer having the entire selector chain use {@link createShallowResultSelectorV2 | `createShallowResultSelectorV2`} instead
 *
 * Uses the newer weak map memoization
 */
export const createShallowInputsAndResultSelectorV2 =
  createDraftSafeSelectorCreator({
    ...selectorV2Options,
    memoizeOptions: {
      resultEqualityCheck: shallowEqual,
    },
    argsMemoizeOptions: {
      resultEqualityCheck: shallowEqual,
    },
  });

/**
 * WARNING: Deep equality checks can be expensive. This should only be used with
 * data that is not deeply nested.
 *
 * Creates a selector that compares the result of its output selector with deep
 * equality. Same as passing `memoizeOptions.resultEqualityCheck: deepEqual`
 * to {@link createSelectorV2 | `createSelectorV2`}.
 *
 * This will have the same effect as calling the selector with
 * `useSelector(selector, deepEqual)`, but bakes in the deepness of the
 * comparison to the selector itself (instead of relying on the consumer). This
 * can also be used when chaining re-select selectors to make an input selector
 * deep compared.
 */
export const createDeepResultSelectorV2 = createDraftSafeSelectorCreator({
  ...selectorV2Options,
  memoizeOptions: {
    resultEqualityCheck: deepEqual,
  },
});
